import { Head, Link, router } from "@inertiajs/react";
import { Building2, Edit, Lock, Trash2, Unlock, Upload } from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout";
import { CompanyLogo } from "@/components/CompanyLogo";
import { GicsClassificationDisplay, type GicsTreeNode } from "@/lib/industry";
import {
  bizminer_admin_organization_account_path,
  bizminer_admin_organization_account_pillar_insight_path,
  bizminer_admin_organization_path,
  upload_bizminer_admin_organization_account_pillar_insight_path,
} from "@/routes";
import type {
  BizminerAdminAccount,
  BizminerAdminOrganizationList,
  BizminerIndustry,
  BizminerPillarInsight,
} from "@/types/serializers";
import PageHeader from "../Organizations/_components/PageHeader";
import { EditAccountDialog } from "./_components/EditAccountDialog";

interface Props {
  account: BizminerAdminAccount;
  pillar_insights: BizminerPillarInsight[];
  organization: BizminerAdminOrganizationList;
  gics_tree: GicsTreeNode[];
  industries: BizminerIndustry[];
}

export default function Show({
  account,
  pillar_insights,
  organization,
  gics_tree,
  industries,
}: Props) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const hasUnlockedInsights = pillar_insights.some(
    (insight) => insight.unlocked,
  );

  const handleDeleteInsight = (insight: BizminerPillarInsight) => {
    if (
      confirm(
        `Are you sure you want to reset "${insight.title}"? This will clear all data and improvement levers for this pillar insight.`,
      )
    ) {
      router.delete(
        bizminer_admin_organization_account_pillar_insight_path(
          organization.id,
          account.id,
          insight.id,
        ),
      );
    }
  };

  const handleDeleteAccount = () => {
    if (
      confirm(
        `Are you sure you want to delete "${account.name}"? This will also delete all associated pillar insights and improvement levers.`,
      )
    ) {
      router.delete(
        bizminer_admin_organization_account_path(organization.id, account.id),
      );
    }
  };

  return (
    <BizminerAdminLayout>
      <Head title={`${account.name} - ${organization.name} - Super Admin`} />

      <div className="min-h-screen">
        <PageHeader
          title={account.name || "Unnamed Account"}
          backUrl={bizminer_admin_organization_path(organization.id)}
          backText={`← Back to ${organization.name}`}
          icon={
            <button
              type="button"
              onClick={() => setIsEditDialogOpen(true)}
              className="relative group cursor-pointer"
              title="Click to edit account"
            >
              <CompanyLogo
                logoUrl={account.logo_url}
                name={account.name}
                className="h-8 w-8"
              />
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-full flex items-center justify-center">
                <Edit className="h-4 w-4 text-white" />
              </div>
            </button>
          }
          actions={
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsEditDialogOpen(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                title="Edit account"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Account
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={hasUnlockedInsights}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  hasUnlockedInsights
                    ? "Cannot delete account with unlocked pillar insights. Reset them first."
                    : `Delete ${account.name}`
                }
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Account
              </button>
            </div>
          }
        />

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            {/* Account Details Card */}
            <div className="bg-card shadow rounded-lg p-6">
              <h3 className="text-lg font-medium text-card-foreground mb-4">
                Account Details
              </h3>
              <div className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Account Name
                  </dt>
                  <dd className="mt-1 text-sm text-card-foreground">
                    {account.name || "Unnamed Account"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Organization
                  </dt>
                  <dd className="mt-1 text-sm text-card-foreground">
                    <Link
                      href={bizminer_admin_organization_path(organization.id)}
                      className="text-primary hover:text-primary/80"
                    >
                      {organization.name}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-muted-foreground">
                    Industry Classification (GICS)
                  </dt>
                  {account.gics_classification ? (
                    <dd className="mt-2 text-sm">
                      <GicsClassificationDisplay
                        classification={account.gics_classification}
                      />
                    </dd>
                  ) : (
                    <dd className="mt-1 text-sm text-muted-foreground italic">
                      Not set
                    </dd>
                  )}
                </div>
              </div>
            </div>

            {/* Pillar Insights Summary */}
            <div className="mt-6 bg-card shadow rounded-lg">
              <div className="px-6 py-4 border-b border-border">
                <h3 className="text-lg font-medium text-card-foreground">
                  Pillar Insights
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Strategic pillars and their improvement levers
                </p>
              </div>

              {pillar_insights.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground">
                  <Building2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p>No pillar insights found for this account</p>
                </div>
              ) : (
                <div className="overflow-hidden">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Slug
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Improvement Levers
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {pillar_insights.map((insight) => (
                        <tr key={insight.id} className="hover:bg-muted/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            {insight.unlocked ? (
                              <Unlock className="h-4 w-4 text-chart-6" />
                            ) : (
                              <Lock className="h-4 w-4 text-muted-foreground" />
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {insight.unlocked ? (
                              <Link
                                href={bizminer_admin_organization_account_pillar_insight_path(
                                  organization.id,
                                  account.id,
                                  insight.id,
                                )}
                                className="text-sm font-medium text-primary hover:text-primary/80 hover:underline"
                              >
                                {insight.title}
                              </Link>
                            ) : (
                              <span className="text-sm font-medium text-muted-foreground">
                                {insight.title}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-muted-foreground font-mono">
                              {insight.slug}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {insight.improvement_lever_count}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-2">
                              <Link
                                href={upload_bizminer_admin_organization_account_pillar_insight_path(
                                  organization.id,
                                  account.id,
                                  insight.id,
                                )}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                                title={`Upload data for ${insight.title}`}
                              >
                                <Upload className="h-3 w-3 mr-1" />
                                Upload
                              </Link>
                              {insight.unlocked && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteInsight(insight)}
                                  className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-white bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                                  title={`Reset ${insight.title}`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <EditAccountDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          account={account}
          organizationId={organization.id}
          gicsTree={gics_tree}
          industries={industries}
        />
      </div>
    </BizminerAdminLayout>
  );
}
